<script setup lang="ts">
import MiiaFwAuthModal from './AuthModal.vue'
import MiiaFwAlertMessageLight from './AlertMessageLight.vue'
import { initializePassword } from '../utils/miiaFwCognito'
import { reactive, ref, useTemplateRef } from 'vue'
import { useLoadingView } from '../composables/useLoadingView'
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue'

/**
 * @module components/miiafw/auth/MiiaFwPasswordInitialize
 * @description 「パスワード初期化」画面の機能
 * @vue-data {Boolean} [myModal = false] モーダル制御。trueのときモーダルを開く
 * @vue-data {String} [userName = 空白] モーダル内に表示させるユーザID
 * @vue-data {String} [verificationCode = 空白] 検証コード
 * @vue-data {String} [password = 空白] パスワード
 * @vue-data {String} [password2 = 空白] パスワード（再確認）
 */
const resetInput = useTemplateRef<HTMLButtonElement>('resetInput')
const alertMessageLight =
  useTemplateRef<InstanceType<typeof MiiaFwAlertMessageLight>>('alertMessageLight')
const verificationCodeInput = useTemplateRef<HTMLInputElement>('verificationCodeInput')
const confirmDialog = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirmDialog')

const { setLoadingView } = useLoadingView()

const emit = defineEmits<{
  'close-top': []
  'open-signin': [string]
  'open-password-reset': [string]
}>()
const myModal = ref(false)
const userName = ref('')
const verificationCode = ref('')
const password = ref('')
const password2 = ref('')
/**
 * フォームの表示制御のためのオブジェクト。
 * hasErrorはフォームの見栄えをAlertにし、
 * alertMessageはフォームの横にアラートメッセージを表示する
 */
const alertView = reactive<{ [key: string]: { hasError: boolean; alertMessage: string } }>({
  /** 検証コード */
  vc: { hasError: false, alertMessage: '' },
  /** パスワード */
  pw: { hasError: false, alertMessage: '' },
  /** 再入力されたパスワード */
  pw2: { hasError: false, alertMessage: '' }
})

const resetAlertView = () => {
  Object.keys(alertView).forEach(key => {
    alertView[key].hasError = false
    alertView[key].alertMessage = ''
  })
}

/**
 * ユーザIDが入った状態のモーダルを開く
 * @param argUserName ユーザIDが定義済みのユーザIDならばモーダルに表示する引数
 */
const openModal = (argUserName: string): void => {
  if (argUserName.length > 0) {
    userName.value = argUserName
  }
  myModal.value = true
}

/**
 * モーダル内のユーザID、認証コード、パスワード、パスワード（再確認）、エラー表示を消去した後、モーダルを閉じる
 */
const closeMyModal = (): void => {
  userName.value = ''
  verificationCode.value = ''
  password.value = ''
  password2.value = ''
  resetAlertView()
  myModal.value = false
}

/**
 * 一番上のモーダルを閉じる。＜MiiaFwAuthModal＞タグを指定することで、特定の操作に対応付けることができる
 */
const closeModal = (): void => {
  closeMyModal()
  emit('close-top')
}

/**
 * モーダル上でTab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e - キーボードイベント
 */
const firstTab = (e: KeyboardEvent): void => {
  e.preventDefault()
  e.stopPropagation()
  resetInput.value?.focus()
}

/**
 * モーダル上でShift+Tab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e - キーボードイベント
 */
const lastTab = (e: KeyboardEvent): void => {
  // Shift + Tabは、Tabの遡りなのでデフォルトイベントのまま
  if (!e.shiftKey) {
    e.preventDefault()
    e.stopPropagation()
    verificationCodeInput.value?.focus()
  }
}

/**
 * モーダル内のユーザID、認証コード、パスワード、パスワード（再確認）、エラー表示を消去
 */
const doReset = (): void => {
  alertMessageLight.value?.deleteAlertMessage()
  verificationCode.value = ''
  password.value = ''
  password2.value = ''
  resetAlertView()
  verificationCodeInput.value?.focus()
}

/**
 * 認証コードとパスワード、パスワード（再確認）のエラー表示機能。表示されているエラーを初期化し、各項目に対してバリデーションチェックを行う。問題箇所があったらエラーを表示する。問題が無かったらmodule:modules/miiafw/MiiaFwCognito#initializePasswordへユーザIDと認証コード、パスワードを渡し、モーダルを閉じる
 */
const doSubmit = (): void => {
  // エラー表示初期化
  resetAlertView()
  // バリデーションチェック
  const errorList: string[] = []
  if (verificationCode.value.length < 1) {
    alertView.vc.hasError = true
    alertView.vc.alertMessage = 'verification codeは必須項目です'
  }
  if (password.value.length < 1) {
    alertView.pw.hasError = true
    alertView.pw.alertMessage = 'New passwordは必須項目です'
  }
  if (password2.value.length < 1) {
    alertView.pw2.hasError = true
    alertView.pw2.alertMessage = 'Re-enter new passwordは必須項目です'
  }
  // バリデーションチェック
  if (
    password.value.length > 0 &&
    password2.value.length > 0 &&
    password.value !== password2.value
  ) {
    alertView.pw.hasError = true
    alertView.pw2.hasError = true
    errorList.push('パスワードが不一致です。')
  }
  if (errorList.length > 0) {
    alertMessageLight.value?.addAlertMessage(errorList.join('\n'))
  } else if (!alertView.vc.hasError && !alertView.pw.hasError && !alertView.pw2.hasError) {
    // 処理中表示
    setLoadingView(true)
    const un = userName.value
    // パスワード初期化処理
    initializePassword(un, verificationCode.value, password.value)
      .then(async () => {
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
        closeMyModal()
        await confirmDialog.value?.openModal()
      })
      .catch(error => {
        if (typeof error.message === 'object') {
          if (
            error.message.errorMessage.match(/username/) ||
            error.message.errorMessage.match(/User/) ||
            error.message.errorMessage.match(/USERNAME/) ||
            error.message.errorMessage.match(/Username/)
          ) {
            // パスワードリセット要求画面を開く
            closeMyModal()
            emit('open-password-reset', un)
          }
          if (error.message.errorMessage.match(/verification/) && error.message.match(/code/)) {
            alertView.vc.hasError = true
          }
          if (
            error.message.errorMessage.match(/Password/) ||
            error.message.errorMessage.match(/password/)
          ) {
            alertView.pw.hasError = true
            alertView.pw2.hasError = true
          }
          alertMessageLight.value?.addAlertMessage('・' + error.message.errorMessage)
        } else {
          alertMessageLight.value?.addAlertMessage(error.message)
        }
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
      })
  }
}
defineExpose({
  // data
  myModal,
  userName,
  verificationCode,
  password,
  password2,
  alertView,

  // methods
  openModal,
  closeMyModal,
  closeModal,
  firstTab,
  lastTab,
  doReset,
  doSubmit
})
</script>

<template>
  <!-- @closeを指定すると枠外クリック時にモーダルウィンドウを閉じる。 -->
  <!-- <MyModal @close="closeModal" v-if="modal"> -->
  <MiiaFwAuthModal v-if="myModal" @push-ctl-delete="doReset">
    <!-- default スロットコンテンツ -->
    <h4 style="font-size: 1.1rem; font-weight: 500">パスワード初期化</h4>
    <div style="padding: 30px 10px 30px 10px; margin-bottom: 10px; border: 1px solid #bcbcbc">
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.vc.hasError }"
      >
        <label class="control-label">
          検証コード
          <span v-if="alertView.vc.alertMessage !== ''" class="ff-alert-msg">
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.vc.alertMessage }}
          </span>
        </label>
        <input
          ref="verificationCodeInput"
          v-model="verificationCode"
          class="form-control ff-frm-ctrl"
          tabindex="1"
          v-focus
          autocomplete="off"
          @keydown.shift.tab="firstTab"
        />
      </div>
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.pw.hasError }"
      >
        <label class="control-label">
          新しいパスワード
          <div
            v-if="alertView.pw.alertMessage !== ''"
            class="ff-alert-msg"
            style="margin-left: 0px"
          >
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.pw.alertMessage }}
          </div>
        </label>
        <input
          type="password"
          v-model="password"
          class="form-control ff-frm-ctrl"
          tabindex="2"
          autocomplete="new-password"
        />
      </div>
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.pw2.hasError }"
      >
        <label class="control-label">
          もう一度新しいパスワードを入力してください。
          <div
            v-if="alertView.pw2.alertMessage !== ''"
            class="ff-alert-msg"
            style="margin-left: 0px"
          >
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.pw2.alertMessage }}
          </div>
        </label>
        <input
          type="password"
          v-model="password2"
          class="form-control ff-frm-ctrl"
          tabindex="3"
          autocomplete="new-password"
        />
      </div>
      <div style="text-align: right">
        <button
          class="btn ff-btn-primary"
          ref="resetInput"
          tabindex="4"
          @click="doSubmit"
          @keydown.tab="lastTab"
        >
          <font-awesome-icon icon="edit" />
          &nbsp;パスワード更新
        </button>
      </div>
    </div>
    <MiiaFwAlertMessageLight ref="alertMessageLight"></MiiaFwAlertMessageLight>
    <!-- /default -->
    <!-- footer スロットコンテンツ -->
    <template #slot> </template>
  </MiiaFwAuthModal>
  <ConfirmDialog
    ref="confirmDialog"
    title="パスワードリセット"
    message="パスワードが変更されました。"
  ></ConfirmDialog>
</template>

<style lang="scss" scoped>
:deep(#miiafwauthmodal-close) {
  visibility: hidden;
}
</style>
